import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Menu, MenuItem, Typography } from "@mui/material";
import MoreVert from "@mui/icons-material/MoreVert";

type UserManagementAction = {
  value: string;
  label: string;
};

type UserManagementActionsMenuProps = {
  actions: UserManagementAction[];
  onAction?: (_action: string) => void;
};

const UserManagementActionsMenu = ({
  actions,
  onAction,
}: UserManagementActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasNoActions = !actions.length;

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleActionClick = useCallback(
    (action: string) => {
      handleClose();
      onAction?.(action);
    },
    [handleClose, onAction]
  );

  if (hasNoActions) {
    return (
      <Typography component="span" className="text-muted" variant="body1">
        No actions
      </Typography>
    );
  }

  return (
    <>
      <IconButton size="small" onClick={handleOpen} aria-label="User actions">
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.value}
            className="small"
            onClick={() => handleActionClick(action.value)}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

UserManagementActionsMenu.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onAction: PropTypes.func,
};

export default UserManagementActionsMenu;
